import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LuSearch, LuX, LuCheck } from "react-icons/lu";
import TransporterLayout from "../../components/TransporterLayout/TransporterLayout";
import WonBidDrawer from "../../components/WonBidDrawer/WonBidDrawer";
import { fetchOpenLoads, fetchMyBids, fetchMyWonBids, fetchMyTrips, getTokenPayload } from "../../lib/directus";
import styles from "./TransporterDashboard.module.css";

function mapWonBid(load, currentUserId) {
  const batch  = load.order?.batch || {};
  const wonBid = (load.bids || []).find(b => b.user_created === currentUserId)
              || (load.bids || [])[0];
  return {
    _id:              load.id,
    _loadId:          load.id,
    orderId:          load.order?.id || null,
    loadId:           load.load_id ? `#${load.load_id}` : `#${String(load.id || '').slice(0, 8).toUpperCase()}`,
    pickup:           load.pickup_location || '-',
    delivery:         load.delivery_location || '-',
    cargo:            batch.commodity
                        ? `${batch.commodity}${batch.grade ? ` (${batch.grade})` : ''}`
                        : '-',
    quantity:         batch.quantity != null ? `${batch.quantity}${batch.unit_of_measure ? ' ' + batch.unit_of_measure : ' MT'}` : '-',
    bid:              wonBid?.bid_amount ? `R${parseFloat(wonBid.bid_amount).toLocaleString()}` : '-',
    assignBy:         load.delivery_deadline
                        ? new Date(load.delivery_deadline).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })
                        : '-',
    distance:         '-',
    estimatedTime:    '-',
    pickupDate:       load.pickup_date
                        ? new Date(load.pickup_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '-',
    pickupWindow:     '-',
    deliveryDeadline: load.delivery_deadline
                        ? new Date(load.delivery_deadline).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '-',
    seller:           '-',
    sellerRating:     0,
    sellerTrades:     0,
  };
}

function mapActiveTransport(load) {
  const batch = load.order?.batch || {};
  const STATUS_LABEL = {
    transport_sourced: 'Transport sourced',
    pending_dispatch: 'Pending dispatch',
    pickup_completed: 'Pickup completed',
    in_transit: 'In transit',
    delivered: 'Delivered',
  };
  const fulfillmentStatus = load.order?.fulfillment_status;
  return {
    id:       load.id,
    loadId:   load.load_id ? `#${load.load_id}` : `#${String(load.id || '').slice(0, 8).toUpperCase()}`,
    pickup:   load.pickup_location || '-',
    delivery: load.delivery_location || '-',
    driver:   load.assigned_driver?.name || '-',
    eta:      '-',
    cargo:    batch.commodity || '-',
    status:   STATUS_LABEL[fulfillmentStatus] || 'In transit',
  };
}

function fmtRevenue(n) {
  if (n >= 1_000_000) return `R${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `R${(n / 1_000).toFixed(0)}K`;
  return `R${n.toLocaleString()}`;
}

function pad2(n) { return String(n).padStart(2, '0'); }

function SuccessModal({ message, onClose }) {
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose}>
          <LuX size={18} />
        </button>
        <div className={styles.successIcon}>
          <LuCheck size={32} color="white" />
        </div>
        <p className={styles.successMsg}>{message}</p>
      </div>
    </div>
  );
}

export default function TransporterDashboard() {
  const navigate = useNavigate();
  const [wonBids, setWonBids]               = useState([]);
  const [activeTransports, setActiveTransports] = useState([]);
  const [stats, setStats]                   = useState({ available: 0, activeBids: 0, inTransit: 0, revenueMTD: 0 });
  const [loading, setLoading]               = useState(true);
  const [selectedWonBid, setSelectedWonBid] = useState(null);
  const [showSuccess, setShowSuccess]       = useState(false);

  const loadData = useCallback(() => {
    const token = localStorage.getItem('access_token');
    const payload = getTokenPayload(token);
    const currentUserId = payload?.id || payload?.sub || null;

    Promise.allSettled([
      fetchOpenLoads(),
      fetchMyBids(),
      fetchMyWonBids(),
      fetchMyTrips(),
    ]).then(([loadsRes, bidsRes, wonRes, tripsRes]) => {
      const loads    = loadsRes.status  === 'fulfilled' ? loadsRes.value  : [];
      const bids     = bidsRes.status   === 'fulfilled' ? bidsRes.value   : [];
      const wonData  = wonRes.status    === 'fulfilled' ? wonRes.value    : [];
      const trips    = tripsRes.status  === 'fulfilled' ? tripsRes.value  : [];

      const available  = loads.filter(l => (l.bidding_status || '').toLowerCase() === 'open').length;
      const activeBids = bids.filter(b => b.status === 'Pending').length;
      const inTransit  = trips.filter(l =>
        ['transport_sourced', 'pending_dispatch', 'pickup_completed', 'in_transit']
          .includes(l.order?.fulfillment_status)
      ).length;

      const now = new Date();
      const revenueMTD = wonData.reduce((s, load) => {
        const myBid = (load.bids || []).find(b => b.user_created === currentUserId) || (load.bids || [])[0];
        if (!myBid) return s;
        const d = new Date(load.date_updated || load.date_created);
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return s;
        return s + (parseFloat(myBid.bid_amount) || 0);
      }, 0);

      setStats({ available, activeBids, inTransit, revenueMTD });
      setWonBids(wonData.map(l => mapWonBid(l, currentUserId)));

      const activeTrans = trips.filter(l =>
        ['transport_sourced', 'pending_dispatch', 'pickup_completed', 'in_transit']
          .includes(l.order?.fulfillment_status)
      );
      setActiveTransports(activeTrans.map(mapActiveTransport));
      setLoading(false);
    });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <TransporterLayout>
      <div className={styles.page}>
        {/* Page header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Dashboard</h1>
          <button className={styles.viewLoadBtn} onClick={() => navigate("/loads")}>
            <LuSearch size={14} />
            View load board
          </button>
        </div>

        {/* Stat cards */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pad2(stats.available)}</p>
            <p className={styles.statLabel}>available loads</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pad2(stats.activeBids)}</p>
            <p className={styles.statLabel}>active bids</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{pad2(stats.inTransit)}</p>
            <p className={styles.statLabel}>in transit</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{fmtRevenue(stats.revenueMTD)}</p>
            <p className={styles.statLabel}>revenue (MTD)</p>
          </div>
        </div>

        {loading && <p className={styles.loadingText}>Loading...</p>}

        {!loading && (
          <>
            {/* New bids won */}
            {wonBids.length > 0 && (
              <>
                <p className={styles.sectionTitle}>New bids won</p>
                <div className={styles.cardsGrid}>
                  {wonBids.map((bid) => (
                    <div key={bid._id} className={styles.wonCard} onClick={() => setSelectedWonBid(bid)}>
                      <div className={styles.wonCardHeader}>
                        <div className={styles.cardTop}>
                          <span className={styles.loadIdText}>{bid.loadId}</span>
                          <span className={styles.wonBadge}>Won</span>
                        </div>
                      </div>
                      <div className={styles.wonCardBody}>
                        <div className={styles.routeRow}>
                          <div className={styles.pickupCol}>
                            <span className={styles.routeLabel}>PICKUP</span>
                            <span className={styles.routeName}>{bid.pickup}</span>
                          </div>
                          <span className={styles.routeArrow}>→</span>
                          <div className={styles.deliveryCol}>
                            <span className={styles.routeLabel}>DELIVERY</span>
                            <span className={styles.routeName}>{bid.delivery}</span>
                          </div>
                        </div>
                        <div className={styles.cardDivider} />
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Cargo</span>
                          <span className={styles.infoValue}>{bid.cargo}</span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Your bid</span>
                          <span className={styles.oliveValue}>{bid.bid}</span>
                        </div>
                        <div className={styles.assignBox}>
                          <span className={styles.assignText}>
                            Assign truck by {bid.assignBy}
                          </span>
                          <button
                            className={styles.assignBtn}
                            onClick={(e) => { e.stopPropagation(); setSelectedWonBid(bid); }}
                          >
                            Assign truck
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Active transports */}
            {activeTransports.length > 0 && (
              <>
                <p className={styles.sectionTitle}>Active transports</p>
                <div className={styles.cardsGrid}>
                  {activeTransports.map((t) => (
                    <div key={t.id} className={styles.transportCard}>
                      <div className={styles.wonCardHeader}>
                        <div className={styles.cardTop}>
                          <span className={styles.loadIdText}>{t.loadId}</span>
                          <span className={styles.inTransitBadge}>{t.status}</span>
                        </div>
                      </div>
                      <div className={styles.wonCardBody}>
                        <div className={styles.routeRow}>
                          <div className={styles.pickupCol}>
                            <span className={styles.routeLabel}>PICKUP</span>
                            <span className={styles.routeName}>{t.pickup}</span>
                          </div>
                          <span className={styles.routeArrow}>→</span>
                          <div className={styles.deliveryCol}>
                            <span className={styles.routeLabel}>DELIVERY</span>
                            <span className={styles.routeName}>{t.delivery}</span>
                          </div>
                        </div>
                        <div className={styles.cardDivider} />
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>Driver</span>
                          <span className={styles.infoValue}>{t.driver}</span>
                        </div>
                        <div className={styles.infoRow}>
                          <span className={styles.infoLabel}>ETA</span>
                          <span className={styles.infoValue}>{t.eta}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {wonBids.length === 0 && activeTransports.length === 0 && (
              <p className={styles.emptyText}>No active bids or transports yet.</p>
            )}
          </>
        )}
      </div>

      <WonBidDrawer
        bid={selectedWonBid}
        onClose={() => setSelectedWonBid(null)}
        onAssigned={() => {
          setSelectedWonBid(null);
          setShowSuccess(true);
          loadData();
        }}
      />

      {showSuccess && (
        <SuccessModal
          message="Truck assigned successfully!"
          onClose={() => setShowSuccess(false)}
        />
      )}
    </TransporterLayout>
  );
}
